import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Dropdown from "../components/Dropdown";
import { useTranslation } from "react-i18next";
import { createArray } from "./analyseData/columns.config";
import {
  uploadSelectedSheet,
  getPlantDetails,
  getSheetColumns,
} from "../network/cipAPI";
import {
  storeFileId,
  updateSelectedTrain,
  storeDefaultInformation,
} from "../Store/TemplateStore/template.action";
import { extractPlantId, getDatasetDetails } from "../utils/Utils";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../components/Notifications";
import { useUnsavedChanges } from "../hooks/useUnsavedChanges";

const FileUpload = React.forwardRef(
  ({
    setFileId,
    setColumnOptions,
    fileInfo,
    selectedTrain,
    config,
    setMappedColumn,
    setSheetNames,
    setMappedReference,
    sheetNames,
    selectedSheet,
    setSelectedSheet,
    setSelectedTrain,
    setSelectedCIPFileName,
    setHasRecurrentCIPs,
    disableTrain,
    setIsLoading,
    setIsAppendFile,
    setAppendFileList,
    setAppendStatus,
    hasUnsavedChanges,
    setNormSelectionType,
    setDatasetDetails,
  }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [selectedTrainOption, setSelectedTrainOption] =
      useState(selectedTrain);
    const plantId = extractPlantId();
    useEffect(() => {
      setSelectedTrainOption(selectedTrain);
    }, [selectedTrain]);

    const resetState = () => {
      setFileId(null);
      setMappedColumn([]);
      setMappedReference(null);
      setIsLoading(false);
    };

    const { setShowDialog, allowActionRef, pendingActionRef } =
      useUnsavedChanges();

    const handleTrainOptionChange = async (value) => {
      if (Number(value) === Number(selectedTrain)) return;
      if (hasUnsavedChanges && allowActionRef.current === false) {
        setShowDialog(true);
        pendingActionRef.current = () => handleTrainOptionChange(value);
        return;
      }
      setSelectedTrainOption(value);
      setSelectedCIPFileName(null);
      setHasRecurrentCIPs(false);
      setIsAppendFile(false);
      setAppendFileList([]);
      setIsLoading(true);

      if (!value) {
        resetState();
        setSelectedTrain(null);
        setSelectedSheet(null);
        return;
      }
      try {
        const response = await getPlantDetails(plantId, true, value);
        if (response && response.data) {
          const {
            file,
            parent_file_name,
            default_cip,
            col_mapping,
            sel_train,
            bulk_cip_file_name,
            has_recurrent_cips,
            name: plantName,
            water_type,
            owner_username,
            sheet_names,
            ref_condition,
            isAppended,
            append_files,
            norm_selection_type,
          } = response.data;
          let file_id, sheet_name, min_date, max_date;
          if (file) {
            ({ file_id, sheet_name, min_date, max_date } = file);
          }

          setFileId(file_id);
          const fileInfo1 = {
            file_id,
            sheetName: sheet_name,
            fileName: parent_file_name,
          };
          dispatch(storeFileId(fileInfo1));

          if (default_cip) {
            dispatch(storeDefaultInformation(default_cip));
          }
          setMappedColumn(col_mapping);
          setSelectedTrain(sel_train);
          setSelectedCIPFileName(bulk_cip_file_name);
          setHasRecurrentCIPs(has_recurrent_cips);
          setDatasetDetails(
            getDatasetDetails({
              owner_username,
              plantName,
              water_type,
              min_date,
              max_date,
            }),
          );

          setIsAppendFile(isAppended);
          setAppendFileList(append_files);
          setAppendStatus("success");
          setNormSelectionType(norm_selection_type);
          dispatch(updateSelectedTrain(sel_train));
          setSheetNames(sheet_names);
          setSelectedSheet(sheet_name);
          setMappedReference(ref_condition[0]);
          if (file_id) {
            const columns = await getSheetColumns(file_id, sel_train);
            if (columns && columns.data) {
              setColumnOptions(columns.data?.cols);
            }
          }
          if (sheet_names && sheet_names.length === 1) {
            if (!file_id) {
              const response = await uploadSelectedSheet(
                value,
                encodeURIComponent(fileInfo.fileName),
                sheet_names[0],
              );
              setColumnOptions(response?.cols);
              setFileId(response?.file_id);
              dispatch({
                type: "STORE_FILE_ID",
                payload: {
                  file_id: response?.file_id,
                },
              });
            }
          }
        }
      } catch (err) {
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.somethingWrong"),
          "",
        );
        resetState();
        setSelectedSheet(null);
        setSelectedCIPFileName(null);
        setHasRecurrentCIPs(false);
        setIsAppendFile(false);
      } finally {
        setIsLoading(false);
      }
    };

    const handleOptionChange = async (value) => {
      if (value === selectedSheet) return;
      setSelectedSheet(value);
      if (!value) {
        resetState();
        setSelectedSheet(null);
        return;
      }
      setIsLoading(true);
      setMappedColumn([]);
      setMappedReference(null);
      setSelectedCIPFileName(null);
      setHasRecurrentCIPs(false);
      setIsAppendFile(false);
      setAppendFileList([]);
      try {
        const response = await uploadSelectedSheet(
          selectedTrain,
          encodeURIComponent(fileInfo.fileName),
          value,
        );
        setColumnOptions(response?.cols);
        setFileId(response?.file_id);
        dispatch({
          type: "STORE_FILE_ID",
          payload: {
            file_id: response?.file_id,
          },
        });
      } catch (error) {
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.somethingWrong"),
          "",
        );
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <div className="ro-card-wrapper">
        <div className="grid-container-row">
          <Dropdown
            labelText={t("addNewPlant.sel_train")}
            key={"sel_train"}
            options={createArray(config?.trains?.length, "sel_train", true)}
            onChange={(value) => {
              handleTrainOptionChange(value);
            }}
            value={selectedTrainOption}
            disabled={disableTrain}
          />
          {sheetNames?.length > 1 && (
            <Dropdown
              labelText={t("addNewPlant.selSheet")}
              options={sheetNames}
              onChange={(value) => {
                handleOptionChange(value);
              }}
              value={selectedSheet}
              disabled={!selectedTrainOption}
            />
          )}
        </div>
      </div>
    );
  },
);

export default FileUpload;
